/**
 * Types for the Model Pricing & Cost Analysis feature.
 * Used by Configuration pages and TokenStats enhancements.
 */

// ============================================================================
// Token Threshold Types
// ============================================================================

/**
 * Per-session token threshold configuration.
 * All fields are nullable - null means no threshold checking for that metric.
 */
export interface SessionThresholdConfig {
  /** Warning threshold for input tokens in a single session */
  inputTokensWarning: number | null
  /** Critical threshold for input tokens in a single session */
  inputTokensCritical: number | null
  /** Warning threshold for output tokens in a single session */
  outputTokensWarning: number | null
  /** Critical threshold for output tokens in a single session */
  outputTokensCritical: number | null
}

/**
 * Cumulative (overall) token threshold configuration.
 * All fields are nullable - null means no threshold checking for that metric.
 */
export interface OverallThresholdConfig {
  /** Warning threshold for cumulative input tokens */
  inputTokensWarning: number | null
  /** Critical threshold for cumulative input tokens */
  inputTokensCritical: number | null
  /** Warning threshold for cumulative output tokens */
  outputTokensWarning: number | null
  /** Critical threshold for cumulative output tokens */
  outputTokensCritical: number | null
}

/**
 * Container for token threshold settings.
 * Null values mean no enforcement for that scope.
 */
export interface TokenThresholds {
  /** Per-session threshold configuration. Null disables per-session enforcement. */
  session: SessionThresholdConfig | null
  /** Cumulative threshold configuration. Null disables cumulative enforcement. */
  overall: OverallThresholdConfig | null
}

// ============================================================================
// Threshold Status Types (for status endpoints)
// ============================================================================

/** Threshold status value */
export type ThresholdStatusValue = 'green' | 'yellow' | 'red' | 'none'

/**
 * Threshold status for a single model in a session.
 */
export interface ModelThresholdStatus {
  /** Name of the model */
  modelName: string
  /** Status for input tokens */
  inputStatus: ThresholdStatusValue
  /** Status for output tokens */
  outputStatus: ThresholdStatusValue
  /** Actual input token count */
  inputTokens: number
  /** Actual output token count */
  outputTokens: number
  /** Input warning threshold (for display) */
  inputWarning: number | null
  /** Input critical threshold (for display) */
  inputCritical: number | null
  /** Output warning threshold (for display) */
  outputWarning: number | null
  /** Output critical threshold (for display) */
  outputCritical: number | null
}

/**
 * Response from GET /sessions/{sessionId}/token-threshold-status
 */
export interface SessionThresholdStatusResponse {
  /** The session ID */
  sessionId: string
  /** Threshold status for each model used in the session */
  models: ModelThresholdStatus[]
}

/**
 * Threshold status for a model in overall (cumulative) context.
 */
export interface OverallModelThresholdStatus {
  /** Name of the model */
  modelName: string
  /** Status for input tokens */
  inputStatus: ThresholdStatusValue
  /** Status for output tokens */
  outputStatus: ThresholdStatusValue
  /** Cumulative input token count */
  inputTokens: number
  /** Cumulative output token count */
  outputTokens: number
  /** Input warning threshold (for display) */
  inputWarning: number | null
  /** Input critical threshold (for display) */
  inputCritical: number | null
  /** Output warning threshold (for display) */
  outputWarning: number | null
  /** Output critical threshold (for display) */
  outputCritical: number | null
}

/**
 * Response from GET /api/token-stats/threshold-status
 */
export interface OverallThresholdStatusResponse {
  /** Threshold status for each model */
  models: OverallModelThresholdStatus[]
  /** Applied start date filter (or null) */
  startDate: string | null
  /** Applied end date filter (or null) */
  endDate: string | null
}

// ============================================================================
// Model Pricing Types
// ============================================================================

/**
 * Provider options for model pricing.
 */
export const PROVIDER_OPTIONS = [
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'azure', label: 'Azure' },
  { value: 'google', label: 'Google' },
  { value: 'other', label: 'Other' },
] as const

/**
 * Provider type derived from options.
 */
export type Provider = typeof PROVIDER_OPTIONS[number]['value']

/**
 * Model pricing configuration.
 * Stored in MongoDB and managed via Configuration UI.
 */
export interface ModelPricing {
  /** Exact model name as returned by the API (e.g., "claude-sonnet-4-5") */
  modelName: string
  /** Human-readable display name (e.g., "Claude Sonnet 4.5") */
  displayName: string
  /** Provider identifier */
  provider: Provider
  /** Price per 1M input tokens in USD */
  inputPricePer1M: number
  /** Price per 1M output tokens in USD */
  outputPricePer1M: number
  /** Price per 1M cache read tokens in USD (optional, defaults to input price) */
  cacheReadPricePer1M?: number | null
  /** Price per 1M cache write tokens in USD (optional, defaults to output price) */
  cacheWritePricePer1M?: number | null
  /** Token consumption thresholds (optional) */
  thresholds?: TokenThresholds | null
  /** Currency code (always "USD" for now) */
  currency: string
  /** When the pricing was created */
  createdAt: string
  /** When the pricing was last updated */
  updatedAt: string
}

/**
 * Response from GET /api/configuration/model-pricing
 */
export interface ModelPricingListResponse {
  items: ModelPricing[]
}

/**
 * Request body for POST /api/configuration/model-pricing
 */
export interface CreateModelPricingRequest {
  /** Exact model name as returned by the API */
  modelName: string
  /** Human-readable display name */
  displayName: string
  /** Provider identifier */
  provider: Provider
  /** Price per 1M input tokens in USD */
  inputPricePer1M: number
  /** Price per 1M output tokens in USD */
  outputPricePer1M: number
  /** Price per 1M cache read tokens in USD (optional) */
  cacheReadPricePer1M?: number | null
  /** Price per 1M cache write tokens in USD (optional) */
  cacheWritePricePer1M?: number | null
  /** Token consumption thresholds (optional) */
  thresholds?: TokenThresholds | null
}

/**
 * Request body for PUT /api/configuration/model-pricing
 * All fields except modelName are optional for partial updates.
 */
export interface UpdateModelPricingRequest {
  /** Exact model name as returned by the API */
  modelName: string
  /** Human-readable display name */
  displayName?: string
  /** Provider identifier */
  provider?: Provider
  /** Price per 1M input tokens in USD */
  inputPricePer1M?: number
  /** Price per 1M output tokens in USD */
  outputPricePer1M?: number
  /** Price per 1M cache read tokens in USD (optional) */
  cacheReadPricePer1M?: number | null
  /** Price per 1M cache write tokens in USD (optional) */
  cacheWritePricePer1M?: number | null
  /** Token consumption thresholds (optional) */
  thresholds?: TokenThresholds | null
}

/**
 * Cost breakdown for a single model (used in token stats).
 */
export interface ModelCost {
  /** Model name */
  modelName: string
  /** Display name (if pricing exists) */
  displayName?: string
  /** Provider (if pricing exists) */
  provider?: string
  /** Input tokens used */
  inputTokens: number
  /** Output tokens used */
  outputTokens: number
  /** Cost of input tokens */
  inputCost: number
  /** Cost of output tokens */
  outputCost: number
  /** Total cost for this model */
  totalCost: number
}

/**
 * Cost calculation result added to token stats responses.
 */
export interface CostCalculationResult {
  /** Total estimated cost across all models */
  totalEstimatedCost: number | null
  /** Currency code (e.g., "USD") */
  currency: string | null
  /** Cost breakdown per model */
  costByModel: ModelCost[]
  /** Models that have usage but no pricing configured */
  modelsWithoutPricing: string[]
}
